import React, { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Configure,
  InstantSearch,
  Pagination,
  ScrollTo
} from 'react-instantsearch-dom'
import {
  connectHits,
  connectStateResults
} from 'react-instantsearch/connectors'
import CategorySelectFilter from 'components/search/CategorySelectFilter'
import CurrentRefinements from 'components/search/CurrentRefinements'
import Filter from 'components/search/Filter'
import ListFull from 'components/search/ListFull'
import LocationFilter from 'components/search/LocationFilter'
import MobileFilters from 'components/search/MobileFilters'
import SchemaOrg from 'components/search/SchemaOrg'
import SearchBox from 'components/search/SearchBox'
import SortBy from 'components/search/SortBy'
import Stats from 'components/search/Stats'
import AlgoliaHelper from 'lib/AlgoliaHelper'
import algoliasearch from 'algoliasearch'
import 'instantsearch.css/themes/algolia.css'
import SearchMap from 'components/geo/leaflet/SearchMap'
import ShowFiltersButtonMobile from 'components/search/ShowFiltersButtonMobile'
import ClearRefinementsMobile from 'components/search/ClearRefinementsMobile'
import { APIProvider } from '@vis.gl/react-google-maps/dist/index.umd'
import qs from 'qs'
import createURL from 'lib/Search'
import classNames from 'classnames'

const SearchPage = ({
  initialHits,
  initialFilters,
  searchableCategories,
  searchState,
  searchResults,
  showMap,
  setShowMap,
  locationRadius,
  hits,
  urls,
  stickyMobileFilters
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const openMobileMenu = useCallback(() => {
    setShowMobileMenu(true)
  }, [])

  const topRef = useRef(null)

  // Get current page hits (array only) for ListFull and SearchMap
  const getCurrentPageHits = () => {
    // Prefer hits from connectHits (paginated), fallback to search results
    if (hits && hits.length > 0) return hits
    if (!searchResults && !initialHits) return []
    return (
      (isFiltering() ? searchResults || initialHits : initialHits).hits || []
    )
  }

  const currentResults = () => {
    if (!searchResults && !initialHits)
      return { hits: [], nbHits: 0, facets: {} }

    return isFiltering() ? searchResults || initialHits : initialHits
  }

  const isFiltering = () => {
    return Object.keys(searchState).length > 0
  }

  const locationFilter = () => (
    <LocationFilter
      initialFilters={initialFilters}
      defaultRefinement={getInitialGeoArea()}
      locationRadius={locationRadius}
    />
  )

  const getInitialGeoArea = () => {
    const geo = initialFilters.around

    if (!geo || typeof window === 'undefined') return

    const bounds = L.latLng(geo.lat, geo.lng).toBounds(geo.radius * 2 * 1000) // the params is a diameter

    return {
      northEast: bounds._northEast,
      southWest: bounds._southWest
    }
  }

  const featureFilter = () => {
    const eventFeatures = AlgoliaHelper.mapFromOriginalRefinementList(
      currentResults().facets.as_event_features,
      initialFilters.as_event_features
    )
    return (
      <Filter
        attribute="as_event_features"
        defaultLabel="Type d'évènement"
        defaultRefinement={initialFilters.as_event_features}
        initialItems={eventFeatures}
      />
    )
  }

  const categorySelectFilter = () => (
    <CategorySelectFilter
      initialItems={searchableCategories}
      attribute="aa_categories"
      defaultRefinement={initialFilters.aa_categories}
      operator="and"
      limit={200}
    />
  )

  const searchBox = () => (
    <SearchBox defaultRefinement={initialFilters.text_search} />
  )
  const currentRefinement = () => <CurrentRefinements clearsQuery />
  const clearRefinementsMobile = () => <ClearRefinementsMobile clearsQuery />

  // Function to add promo item to hits without mutation
  const getHitsWithPromo = useCallback(
    (hits) => {
      if (!hits || hits.length === 0) {
        return hits || []
      }

      if (hits.find((hit) => hit.type === 'promo')) {
        return hits
      }

      const promo = { type: 'promo' }
      const currentCategories =
        searchState.refinementList?.aa_categories ||
        (initialFilters.aa_categories && initialFilters.aa_categories[0])
      const currentFeature =
        searchState.refinementList?.as_event_features ||
        (initialFilters.as_event_features &&
          initialFilters.as_event_features[0])

      if (currentCategories?.includes('Montgolfière')) {
        promo.node = 'hot-air-balloon'
      } else if (currentCategories?.includes('ULM')) {
        promo.node = 'ulm'
      } else if (currentCategories?.includes('Vol en hélicoptère')) {
        promo.node = 'helicopter'
      } else if (currentCategories?.includes('Parapente')) {
        promo.node = 'paragliding'
      } else if (currentCategories?.includes("Saut à l'élastique")) {
        promo.node = 'bungee-jumping'
      } else if (currentCategories?.includes('Saut en parachute')) {
        promo.node = 'parachute'
      } else if (currentCategories?.includes('Canyoning')) {
        promo.node = 'canyoning'
      } else if (currentCategories?.includes('Stage de survie')) {
        promo.node = 'survival'
      } else if (currentCategories?.includes('Auto')) {
        promo.node = 'racing'
      }

      if (!promo.node) {
        switch (currentFeature) {
          case 'EVJF':
            promo.node = 'bachelorette'
            break
          case 'EVG':
            promo.node = 'bachelor'
            break
          case 'Team building':
            promo.node = 'team'
            break
          default:
            promo.node = 'gift'
            break
        }
      }

      return [hits[0], promo, ...hits.slice(1)]
    },
    [searchState, initialFilters]
  )

  return (
    <APIProvider apiKey={process.env.GOOGLE_API_KEY}>
      <SchemaOrg
        nbHits={currentResults().nbHits}
        ratingsAverage={
          initialHits.facets_stats &&
          initialHits.facets_stats['aa_rating_info.avg']
        }
        ratingsCount={
          initialHits.facets_stats &&
          initialHits.facets_stats['aa_rating_info.count']
        }
        priceInfo={
          initialHits.facets_stats &&
          initialHits.facets_stats['aa_pricing_info.offers.base']
        }>
        <SearchMap
          showMap={showMap}
          setShowMap={setShowMap}
          geoSearchParams={initialFilters.around}
          currentPageHits={getCurrentPageHits()}
          currentPage={currentResults().page}
        />
        <div className="container search-page">
          <ScrollTo>
            <MobileFilters
              showMobileMenu={showMobileMenu}
              closeMobileMenu={() => setShowMobileMenu(false)}
              locationFilter={locationFilter()}
              featureFilter={featureFilter()}
              categoryFilter={categorySelectFilter()}
              searchBox={searchBox()}
              nbHits={currentResults().nbHits}
              clearRefinementsMobile={clearRefinementsMobile()}
            />
          </ScrollTo>

          <div className="row searchPage-Results">
            <div ref={topRef} className="searchPage-content col-sm-12">
              <div
                className={classNames('visible-xs', {
                  'sticky-mobile-footer': stickyMobileFilters
                })}>
                <div className="row">
                  <div className="col-xs-6">
                    <button
                      type="button"
                      className="btn btn-block btn-primary bold"
                      onClick={() => setShowMap(true)}>
                      Afficher la carte
                    </button>
                  </div>
                  <div className="col-xs-6">
                    <ShowFiltersButtonMobile
                      openMobileMenu={openMobileMenu}
                      initialAroundLatLng={initialFilters.aroundLatLng}
                    />
                  </div>
                </div>
              </div>
              <div className="searchPage-topFilters hidden-xs row">
                <div className="col-xs-12 col-sm-3">{searchBox()}</div>
                <div className="col-xs-12 col-sm-3">
                  {categorySelectFilter()}
                </div>
                <div className="col-xs-12 col-sm-3">{locationFilter()}</div>
                <div className="col-xs-12 col-sm-3">{featureFilter()}</div>
              </div>
              <div className="hidden-xs row my-4">
                <div className="col-xs-12">{currentRefinement()}</div>
              </div>
              <div className="content-head">
                <div>
                  <Stats nbHits={currentResults().nbHits} />
                  <button
                    type="button"
                    className="show-map btn btn-md btn-info btn-outline hidden-xs ml-3"
                    onClick={() => setShowMap(true)}>
                    <span className="glyphicon glyphicon-globe mr-1"></span>
                    Afficher la carte
                  </button>
                </div>

                <SortBy
                  defaultRefinement={AlgoliaHelper.getIndexName(
                    'Activity_popularity_desc'
                  )}
                  items={[
                    {
                      value: AlgoliaHelper.getIndexName(
                        'Activity_popularity_desc'
                      ),
                      label: 'Popularité'
                    },
                    {
                      value: AlgoliaHelper.getIndexName('Activity_note_desc'),
                      label: 'Note'
                    }
                  ]}
                  initialTitle={
                    initialFilters.sort_by === 'Activity_note_desc'
                      ? 'Note'
                      : 'Popularité'
                  }
                />
              </div>
              <div className="row search-results-row">
                <ListFull
                  hits={getHitsWithPromo(getCurrentPageHits())}
                  urls={urls}
                />
              </div>
              <Pagination />
            </div>
          </div>
        </div>
      </SchemaOrg>
    </APIProvider>
  )
}

SearchPage.propTypes = {
  initialFilters: PropTypes.object,
  initialHits: PropTypes.object,
  searchState: PropTypes.object,
  searchableCategories: PropTypes.array,
  searchResults: PropTypes.object,
  showMap: PropTypes.bool,
  setShowMap: PropTypes.func,
  locationRadius: PropTypes.number,
  urls: PropTypes.shape({
    bachelorQuoteUrl: PropTypes.string.isRequired,
    bacheloretteQuoteUrl: PropTypes.string.isRequired
  }),
  hits: PropTypes.arrayOf(PropTypes.object),
  updateUrlWithSearchParams: PropTypes.bool.isRequired,
  stickyMobileFilters: PropTypes.bool.isRequired
}

const SearchPageWithResults = connectHits(connectStateResults(SearchPage))

const appId = process.env.ALGOLIA_APP_ID
const apiKey = process.env.ALGOLIA_SEARCH_API_KEY
const searchClient = algoliasearch(appId, apiKey)

const InstantSearchPage = (props) => {
  const [showMap, setShowMap] = useState(false)
  const [searchState, setSearchState] = useState(() => {
    if (typeof window !== 'undefined') {
      const parsedState = qs.parse(window.location.search.slice(1))
      const { configure, ...stateWithoutConfigure } = parsedState
      return stateWithoutConfigure
    }
    return {}
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const onPopState = ({ state }) => {
        if (state) {
          const { configure, ...stateWithoutConfigure } = state.searchState
          setSearchState(stateWithoutConfigure)
        } else {
          setSearchState({})
        }
      }
      window.addEventListener('popstate', onPopState)
      return () => window.removeEventListener('popstate', onPopState)
    }
  }, [])

  const onSearchStateChange = (nextSearchState) => {
    if (typeof window !== 'undefined') {
      const { configure, ...stateWithoutConfigure } = nextSearchState

      if (props.updateUrlWithSearchParams) {
        const newUrl = createURL(stateWithoutConfigure)
        window.history.pushState(
          { searchState: stateWithoutConfigure },
          '',
          newUrl
        )
      }
    }
    setSearchState(nextSearchState)
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={AlgoliaHelper.getIndexName('Activity_popularity_desc')}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}>
      <Configure
        hitsPerPage={props.initialHits.hitsPerPage}
        restrictSearchableAttributes={['name', 'aa_description']}
      />
      <SearchPageWithResults
        {...props}
        showMap={showMap}
        setShowMap={setShowMap}
      />
    </InstantSearch>
  )
}

InstantSearchPage.defaultProps = {
  initialFilters: {}
}

InstantSearchPage.propTypes = {
  initialFilters: PropTypes.object,
  initialHits: PropTypes.object,
  searchableCategories: PropTypes.array,
  locationRadius: PropTypes.number,
  urls: PropTypes.shape({
    bachelorQuoteUrl: PropTypes.string.isRequired,
    bacheloretteQuoteUrl: PropTypes.string.isRequired
  }),
  updateUrlWithSearchParams: PropTypes.bool.isRequired,
  stickyMobileFilters: PropTypes.bool.isRequired
}

export default InstantSearchPage
